import React from 'react';
import { OziraMark } from './OziraMark';

// Shared product header (Figma 66:158). Identity, organization, and project
// context reflect runtime state.
export interface OziraContextBarProps {
  organization: { state: 'LOADING' | 'PERSONAL' | 'ORGANIZATION' | 'UNAUTHORIZED' | 'NO_ORGANIZATION'; tenantId?: string | null };
  projectLabel?: string | null;
  principalLabel?: string | null;
  language: string;
  onLanguageChange?: (lang: string) => void;
  languages?: readonly string[];
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  productSwitcher?: React.ReactNode;
  onAccountClick?: () => void;
  accountLabel?: string;
  utilityItems?: readonly string[];
}

export function OziraContextBar({
  organization, projectLabel, principalLabel, language, onLanguageChange,
  languages = ['en', 'ar'], searchValue, onSearchChange, searchPlaceholder = 'Search',
  productSwitcher, onAccountClick, accountLabel = 'Account settings',
  utilityItems = [],
}: OziraContextBarProps) {
  const orgLabel = organization.state === 'ORGANIZATION'
    ? 'Organization'
    : organization.state === 'PERSONAL' ? 'Personal'
      : organization.state === 'LOADING' ? 'Loading…'
        : organization.state === 'NO_ORGANIZATION' ? 'No organization'
          : 'Unauthorized';

  return (
    <header className="ozira-context-bar" role="banner">
      <div className="ozira-context-bar__identity">
        <OziraMark size={26} />
        <span className="ozira-context-bar__brand">OZIRA</span>
      </div>

      <div className="ozira-context-bar__scope">
        <span className="ozira-badge" data-testid="org-badge">{orgLabel}</span>
        {projectLabel ? <span className="ozira-badge" data-testid="project-badge">{projectLabel}</span> : null}
      </div>

      {productSwitcher}

      {onSearchChange ? (
        <input
          type="search"
          className="ozira-context-bar__search"
          value={searchValue ?? ''}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={searchPlaceholder}
          aria-label={searchPlaceholder}
        />
      ) : null}

      <div className="ozira-context-bar__actions">
        {utilityItems.length ? (
          <div className="ozira-context-bar__utilities" aria-label="Global utilities">
            {utilityItems.map((item) => <span key={item}>{item}</span>)}
          </div>
        ) : null}
        {onLanguageChange ? (
          <select
            className="ozira-context-bar__lang"
            value={language}
            onChange={(e) => onLanguageChange(e.target.value)}
            aria-label="Language"
          >
            {languages.map((code) => <option key={code} value={code}>{code.toUpperCase()}</option>)}
          </select>
        ) : null}
        {onAccountClick ? (
          <button
            type="button"
            className="ozira-account-settings"
            onClick={onAccountClick}
            aria-label={accountLabel}
            title={accountLabel}
          >
            <span className="ozira-avatar" aria-hidden="true">
              {(principalLabel ?? '?').trim().charAt(0).toUpperCase() || '?'}
            </span>
            <span className="ozira-account-settings__label">Settings</span>
          </button>
        ) : (
          <span className="ozira-avatar ozira-avatar--static" aria-label={principalLabel ? `Account: ${principalLabel}` : 'Account'}>
            {(principalLabel ?? '?').trim().charAt(0).toUpperCase() || '?'}
          </span>
        )}
      </div>
    </header>
  );
}
